/**
 * PoiGroupsPage - Página de Grupos de POIs Premium
 * Migrado para padrão Premium Fleet
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CategoryIcon from '@mui/icons-material/Category';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { useEffectAsync } from '../reactHelper';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTableSelection } from '../common/hooks';

// Componentes extraídos
import PoiGroupsTableRow from './components/PoiGroupsTableRow';
import {
  PremiumEmptyState,
  BulkActionsBar,
  BulkDeleteDialog,
} from '../common/components/premium';

const PoiGroupsPage = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Bulk action states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Fetch de dados
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow('/api/poigroups');
      const data = await response.json();
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  // Filtragem + Busca
  const filteredItems = useMemo(() => {
    let result = items;
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (i) =>
          i.name?.toLowerCase().includes(lower) ||
          i.description?.toLowerCase().includes(lower) ||
          i.id?.toString().includes(lower)
      );
    }
    return result;
  }, [items, searchText]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // Bulk selection hook
  const {
    selectedIds,
    selectedCount,
    toggleSelect,
    selectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    isIndeterminate,
    hasSelection,
  } = useTableSelection(filteredItems);

  // Bulk delete handler
  const handleBulkDelete = useCallback(async () => {
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedIds.map((id) =>
          fetchOrThrow(`/api/poigroups/${id}`, { method: 'DELETE' })
        )
      );
      clearSelection();
      setDeleteDialogOpen(false);
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Erro ao excluir grupos:', error);
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedIds, clearSelection]);

  // Handlers
  const handleClearFilters = useCallback(() => {
    setSearchText('');
    setPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    setTimestamp(Date.now());
  }, []);

  // Check if has active filters
  const hasFilters = Boolean(searchText.trim());

  // Header styles for premium table
  const headerCellSx = {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: tokens?.text?.secondary,
    borderBottom: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
    py: 1.5,
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'poiTitle', 'sharedGroups']}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Loading State */}
          {loading && items.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <Typography variant="body1" sx={{ color: tokens?.text?.secondary }}>
                {t('sharedLoading') || 'Carregando...'}
              </Typography>
            </Box>
          ) : (
            <>
              {/* BARRA DE AÇÕES */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 1, sm: 1.5 },
                  alignItems: { xs: 'stretch', md: 'center' },
                  flexShrink: 0,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/settings/pois')}
                    sx={{
                      color: tokens?.text?.primary,
                      borderColor: tokens?.border?.default,
                      '&:hover': { borderColor: tokens?.accent?.primary, bgcolor: `${tokens?.accent?.primary}10` },
                    }}
                  >
                    {t('sharedBack')}
                  </Button>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: tokens?.text?.primary }}>
                    {t('poiGroups')}
                  </Typography>
                </Stack>
                <TextField
                  size="small"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                  placeholder={t('sharedSearch')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: tokens?.accent?.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    maxWidth: 400,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: tokens?.surface?.glass,
                      borderRadius: tokens?.radius?.md,
                      '&:hover': { backgroundColor: tokens?.surface?.hover },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddCircleIcon />}
                  onClick={() => navigate('/settings/poigroup')}
                  sx={{ backgroundColor: tokens?.accent?.primary, '&:hover': { backgroundColor: tokens?.accent?.hover } }}
                >
                  {t('sharedAdd')}
                </Button>
              </Box>

              {/* Results count & Bulk Actions Bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredItems.length} {t('sharedGroups') || 'grupos'}
                  {hasFilters && ` (${t('sharedFiltered') || 'filtrado'})`}
                </Typography>

                {hasSelection && (
                  <BulkActionsBar
                    selectedCount={selectedCount}
                    onClearSelection={clearSelection}
                    onDelete={() => setDeleteDialogOpen(true)}
                    loading={bulkActionLoading}
                    entityName={t('sharedGroups') || 'grupo(s)'}
                  />
                )}
              </Box>

              {/* TABELA */}
              <Paper
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  borderRadius: tokens?.radius?.lg || '12px',
                  backgroundColor: tokens?.surface?.glass,
                  border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                }}
              >
                <TableContainer className="premium-table-container" sx={{ maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ ...headerCellSx, width: 48 }}>
                          <Checkbox
                            indeterminate={isIndeterminate}
                            checked={isAllSelected}
                            onChange={selectAll}
                            sx={{
                              color: tokens?.text?.tertiary,
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: tokens?.accent?.primary },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...headerCellSx, width: 48 }} />
                        <TableCell sx={{ ...headerCellSx, width: 60 }}>ID</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedName')}</TableCell>
                        <TableCell sx={{ ...headerCellSx, width: 100 }}>{t('poiMarkerColor') || 'Cor'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedDescription')}</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>{t('sharedActions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginated.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ border: 0, p: 0 }}>
                            <PremiumEmptyState
                              icon={CategoryIcon}
                              title={t('poiGroupsEmpty') || 'Nenhum grupo cadastrado'}
                              titleFiltered={t('poiGroupsNotFound') || 'Nenhum grupo encontrado'}
                              subtitle={t('poiGroupsEmptyHint') || 'Comece criando seu primeiro grupo de POIs'}
                              subtitleFiltered={t('poiGroupsNotFoundHint') || 'Tente ajustar os filtros de busca'}
                              hasFilters={hasFilters}
                              onClearFilters={handleClearFilters}
                              addPath="/settings/poigroup"
                              addLabel={t('poiGroupAdd') || 'Adicionar Grupo'}
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginated.map((row) => (
                          <PoiGroupsTableRow
                            key={row.id}
                            row={row}
                            onRefresh={handleRefresh}
                            selected={isSelected(row.id)}
                            onSelect={toggleSelect}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {filteredItems.length > 0 && (
                  <TablePagination
                    component="div"
                    count={filteredItems.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage={t('rowsPerPage') || 'Linhas por página'}
                    sx={{
                      borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontSize: '0.8rem',
                        color: tokens?.text?.secondary,
                      },
                    }}
                  />
                )}
              </Paper>
            </>
          )}
        </Box>
      </Box>

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        selectedCount={selectedCount}
        loading={bulkActionLoading}
        entityName={t('sharedGroups') || 'grupo(s)'}
      />
    </PageLayout>
  );
};

export default PoiGroupsPage;
